"use client";

import { useId, useState } from "react";
import { themeClasses } from "@cw/design-tokens/theme";
import { ContainedSection, SectionHeader } from "@cw/ui";
import type { Page } from "@/payload-types";
import { Lexical } from "@/templates/Lexical";
import { CallTrace } from "./CallTrace";
import styles from "./RoutingVerification.module.css";

type CopySection = NonNullable<NonNullable<Page["module"]>["sections"]>[number];
type Verdict = "pass" | "fail" | "skipped";
type Check = { label: string; expected: string; observed: string; verdict: Verdict };
type Scenario = {
  label: string;
  clock: string;
  route: string[];
  checks: Check[];
  finding: boolean;
};

const SCENARIOS: Scenario[] = [
  {
    label: "Business hours",
    clock: "Tuesday, 10:14",
    route: ["Test call initiated", "PSTN connected", "Support selected · option 2", "Support queue", "Agent answered"],
    checks: [
      { label: "Destination", expected: "Support queue", observed: "Support agent reached", verdict: "pass" },
      { label: "Answer time", expected: "Within 20 seconds", observed: "18 seconds", verdict: "pass" },
      { label: "Queue wait", expected: "Within 30 seconds", observed: "12 seconds", verdict: "pass" },
      { label: "Hours routing", expected: "Business-hours route", observed: "Business-hours route followed", verdict: "pass" },
    ],
    finding: false,
  },
  {
    label: "After hours",
    clock: "Tuesday, 19:42",
    route: ["Test call initiated", "PSTN connected", "Closed-hours greeting · option 2", "Retired overflow queue", "Unintended voicemail"],
    checks: [
      { label: "Destination", expected: "Approved after-hours destination", observed: "Retired queue → voicemail", verdict: "fail" },
      { label: "Answer time", expected: "Within 20 seconds", observed: "No answer after 60 seconds", verdict: "fail" },
      { label: "Queue wait", expected: "Within 30 seconds", observed: "No queue wait measurement", verdict: "skipped" },
      { label: "Hours routing", expected: "Approved after-hours route", observed: "Overflow rule targets a retired queue", verdict: "fail" },
    ],
    finding: true,
  },
];

function Status({ verdict }: { verdict: Verdict }) {
  return <span className={`${styles.status} ${styles[verdict]}`}><span aria-hidden="true">{verdict === "pass" ? "✓" : verdict === "fail" ? "!" : "—"}</span>{verdict === "pass" ? "Passed" : verdict === "fail" ? "Failed" : "Not evaluated"}</span>;
}

/** Illustrative evidence, deliberately stable so readers can inspect each result. */
export function RoutingVerificationStage() {
  const [scenarioIndex, setScenarioIndex] = useState(1);
  const reportId = useId();
  const scenario = SCENARIOS[scenarioIndex]!;
  return (
    <div className={styles.report}>
      <header className={styles.header}>
        <div>
          <p className={styles.eyebrow}>Routing verification <span>Illustrative report</span></p>
          <h3 className={styles.title}>Verify each scheduled call against your design.</h3>
        </div>
        <div className={styles.switcher} role="group" aria-label="Example scenario">
          {SCENARIOS.map((item, index) => <button key={item.label} type="button" aria-pressed={scenarioIndex === index} aria-controls={reportId} onClick={() => setScenarioIndex(index)}>{item.label}</button>)}
        </div>
      </header>
      <div id={reportId}>
        <div className={styles.metadata}>
          <span><strong>Scheduled test call</strong> / Support routing</span>
          <span>{scenario.clock} <span aria-hidden="true">·</span> {scenario.label}</span>
        </div>
        <CallTrace key={scenarioIndex} route={scenario.route} finding={scenario.finding} />
        <div className={`${styles.outcome} ${scenario.finding ? styles.finding : ""}`}>
          <span className={styles.outcomeIcon} aria-hidden="true">{scenario.finding ? "!" : "✓"}</span>
          <div><p>{scenario.finding ? "Routing exception identified" : "Routing verified against the approved design"}</p><span>{scenario.finding ? "The closed-hours overflow rule targets a retired queue. Review the finding and its remediation path; changes follow your approval policy." : "All four checks met the configured targets. The result is recorded in audit history."}</span></div>
          <span className={styles.outcomeTag}>{scenario.finding ? "Review required" : "Checks passed"}</span>
        </div>
        <details className={styles.details}>
          <summary>View detailed verification results</summary>
        <div className={styles.evidence}>
          <div className={styles.evidenceHeading}><h4>Verification results</h4><p aria-live="polite" aria-atomic="true">{scenario.label}: {scenario.finding ? "3 failed · 1 not evaluated" : "4 of 4 checks passed"}</p></div>
          <table className={styles.table}>
            <caption className="sr-only">Illustrative {scenario.label.toLowerCase()} results against routing design and SLA targets</caption>
            <thead><tr><th scope="col">Control</th><th scope="col">Expected</th><th scope="col">Observed</th><th scope="col">Result</th></tr></thead>
            <tbody>{scenario.checks.map((check) => <tr key={check.label}>
              <th scope="row">{check.label}</th><td data-label="Expected">{check.expected}</td><td data-label="Observed">{check.observed}</td><td data-label="Result"><Status verdict={check.verdict} /></td>
            </tr>)}</tbody>
          </table>
        </div>

        </details>
      </div>
      <footer className={styles.footer}>Illustrative scenarios and targets. Actual checks follow your approved routing design and SLA requirements.</footer>
    </div>
  );
}

export function RoutingVerification({ section }: { section: CopySection }) {
  const theme = section.theme ?? "paper";
  const t = themeClasses(theme);
  return (
    <ContainedSection theme={theme} id="how-we-verify" compact>
      <div className={styles.intro}>
        <div>
          <SectionHeader eyebrow={section.eyebrow ?? undefined} theme={theme} className="mb-4" />
          {section.lede ? <h2 className={`${t.heading} text-display-sm font-display font-semibold ${styles.heading}`}>{section.lede}</h2> : null}
        </div>
        <div className={`${t.prose} ${t.body} ${styles.copy}`}>{section.body ? <Lexical data={section.body} /> : null}</div>
      </div>
      <RoutingVerificationStage />
    </ContainedSection>
  );
}
